<script>
import { saveAs } from 'file-saver/FileSaver'
import { toArrayBuffer } from 'src/utils'
import { mapActions, mapMutations, mapState } from 'vuex'
import { notify } from 'src/plugins/notify'

const moment = require('moment')

// Same order and labels as the workbook the API builds
const CATEGORIES = [
  { key: 'verified', label: 'Verified', color: 'positive', hint: 'Google confirmed the address as entered' },
  { key: 'fixed', label: 'Fixed', color: 'positive', hint: 'Google\'s correction was accepted and saved' },
  { key: 'fix', label: 'Fix Available', color: 'warning', hint: 'Google suggests a correction that was never applied' },
  { key: 'unverified', label: 'Not Validated', color: 'negative', hint: 'Google could not confirm it, or there is no address' },
  { key: 'unchecked', label: 'Not Checked', color: 'grey', hint: 'Not checked yet, or edited since it was' },
]

export default {
  name: 'AddressValidationReport',
  data () {
    return {
      startDate: null,
      endDate: null,
      running: false,
      result: null,
      categories: CATEGORIES,
    }
  },
  computed: {
    ...mapState({
      addressValidationModal: s => s.shared.addressValidationModal,
    }),
  },
  methods: {
    ...mapMutations(['closeModal']),
    ...mapActions('reports', ['generateAddressValidationReport']),
    async runReport () {
      if (this.running) return

      if (!this.startDate || !this.endDate) {
        notify.create({ message: 'Please enter start and end date', type: 'negative' })
        return
      }

      const payload = {
        start_date: moment(this.startDate).format('YYYY-MM-DD'),
        end_date: moment(this.endDate).format('YYYY-MM-DD'),
      }

      this.running = true
      this.result = null
      const response = await this.generateAddressValidationReport(payload)
      this.running = false

      if (!response || !response.success) {
        notify.create({ message: (response && response.message) || 'Could not run the report.', type: 'negative' })
        return
      }

      this.result = Object.assign({ range: payload }, response.payload)
    },
    download () {
      if (!this.result) return
      const { content, range } = this.result
      saveAs(
        new Blob([toArrayBuffer(content.data)], { type: 'application/octet-stream' }),
        `Address Validation ${range.start_date} to ${range.end_date}.xlsx`
      )
    },
  },
}
</script>

<template>
  <q-modal class="sl-modal"
    id="addressValidationModal"
    :value="addressValidationModal"
    @hide="closeModal('addressValidation')"
  >
    <q-modal-layout>
      <q-toolbar slot="header">
        <q-toolbar-title>
          <q-icon name="fa fa-map-marker" />Address Validation Report
        </q-toolbar-title>
        <q-icon @click.native="closeModal('addressValidation')" name="clear" />
      </q-toolbar>
      <div style="padding:40px 20px">
        <div class="sl-row">
          <div class="sl-col-12">
            <p style="max-width:420px; margin:0 auto" class="text-center">
              How the student addresses stand with Google for classes <b>starting</b> within the date range.
            </p>
          </div>
        </div>
        <div class="sl-row" style="margin:0">
          <div class="sl-col-6">
            <q-datetime stack-label="Start Date *" v-model="startDate" />
          </div>
          <div class="sl-col-6">
            <q-datetime stack-label="End Date *" v-model="endDate" />
          </div>
        </div>

        <div v-if="result" class="address-validation-summary">
          <div class="totals">
            <div><b>{{ result.summary.students }}</b> students in <b>{{ result.summary.classes }}</b> classes</div>
            <div>
              <b>{{ result.summary.classesAffected }}</b>
              {{ result.summary.classesAffected === 1 ? 'class has' : 'classes have' }}
              an address that is not validated or has a fix available
            </div>
          </div>
          <div v-for="category in categories" :key="category.key" class="category" :title="category.hint">
            <span :class="`text-${category.color}`">{{ category.label }}</span>
            <b>{{ result.summary[category.key] }}</b>
          </div>
        </div>
      </div>
      <q-toolbar slot="footer">
        <q-toolbar-title></q-toolbar-title>
        <q-btn color="green" @click.native="runReport()" icon="fa fa-play" label="Run" :loading="running" />&nbsp;
        <q-btn v-if="result" color="primary" outline @click.native="download()" icon="fa fa-download" label="Download" />&nbsp;
        <q-btn outline color="faded" @click.native="closeModal('addressValidation')" label="Cancel" />
      </q-toolbar>
    </q-modal-layout>
  </q-modal>
</template>

<style lang="stylus">
.address-validation-summary
  max-width 420px
  margin 24px auto 0

  .totals
    margin 0 0 12px
    line-height 1.6

  .category
    display flex
    justify-content space-between
    padding 6px 0
    border-bottom 1px solid #eee
</style>
